// @flow

import React, { Component } from "react";
import classNames from "classnames";
import withStyles from "elevate-ui/withStyles";
import Button from "elevate-ui/Button";
import KeyboardArrowDown from "elevate-ui-icons/KeyboardArrowDown";
import transform from "lodash/transform";
import isArray from "lodash/isArray";
import noScroll from "no-scroll";
import moment from "moment";
import SimpleFilters from "./SimpleFilters";
import FilterForm from "./FilterForm";

import { withGlobalContext } from "../common/GlobalContext";

import Pagination from "../common/Pagination";

type $Props = {
  filters: Object,
  activeMLSFeedLabels: Array<Object>,
  activeMLSFeeds: Array<number>,
  advancedSearchFilterOrder: Object,
  children: any,
  cities: Array<Object>,
  classes: Object,
  className: string,
  mlsFeeds: Array<Object>,
  pagination: Object,
  path: string,
  query: Object,
  queryStringified: string,
  searchFormAction: string,
  title: string,
  isMapEnabled: boolean,
  handlePageNavigation: (null | Event, Object) => void,
  handleSearch: (null | Event, Object) => void,
  siteUser: Object,
};

type $State = {
  filterVisible: boolean,
};

class SearchControls extends Component<$Props, $State> {
  constructor(props) {
    super(props);

    this.state = {
      filterVisible: false,
    };
  }

  /**
   * Gets rid of filters with empty strings or -1 values
   */
  cleanFilters(values) {
    return transform(
      values,
      (memo, value, key) => {
        if (
          value !== "" &&
          value !== "-1" &&
          (!Array.isArray(value) || value.length)
        ) {
          memo[key] = value;
        }
      },
      {}
    );
  }

  getInitial({ filter, query }) {
    let value;
    if (query && query[filter.name]) {
      value = query[filter.name];
      //else if because v2 filters have no synonyms
    } else if (filter.synonyms) {
      //Backwards compatibility for synonym (e.g. isRental vs rentalYN)
      filter.synonyms.forEach((synonym) => {
        if (query && query[synonym] && !value) {
          value = query[synonym];
        }
      });
    }

    if (!value && filter.type === "multiselect") {
      return [];
    } else if (!value) {
      return "";
    }
    switch (filter.type) {
      case "text":
      case "select":
      case "buttongroup":
        return value;
      case "dateTime":
        return moment(value).toDate();
      case "multiselect":
        return isArray(value) ? value : [value];
      default:
        return null;
    }
  }

  handleFilterToggle = () => {
    noScroll.toggle();
    this.setState((state) => ({
      filterVisible: !state.filterVisible,
    }));
  };

  renderPagination = () => {
    const {
      pagination,
      path,
      isMapEnabled,
      handlePageNavigation,
      query,
    } = this.props;
    if (isMapEnabled) {
      return null;
    }
    const Component = Pagination;
    return (
      <Component
        path={path}
        pagination={pagination}
        query={query}
        handleSearch={(e, values) => handlePageNavigation(e, values)}
      />
    );
  };

  render() {
    const {
      advancedSearchFilterOrder,
      children,
      classes,
      className,
      siteUser,
    } = this.props;
    const { filterVisible } = this.state;

    const parentClass = classNames(classes.searchControlRoot, className);

    return (
      <div className={parentClass}>
        <div className={classes.searchControlContainer}>
          <div className={classes.top}>
            <div className={classes.topLeft}>
              <SimpleFilters
                {...this.props}
                cleanFilters={this.cleanFilters}
                getInitial={this.getInitial}
                handleFilterToggle={this.handleFilterToggle}
                handleSearch={this.props.handleSearch}
              />
            </div>
            <div className={classes.topRight}>
              <Button
                type="button"
                icon={<KeyboardArrowDown />}
                iconAlign="right"
                onClick={this.handleFilterToggle}
                isSecondary
                className={classes.moreButton}
                color="#b3bac1"
              >
                More
              </Button>
            </div>
          </div>
        </div>
        <FilterForm
          {...this.props}
          advancedSearchFilterOrder={advancedSearchFilterOrder}
          className={classes.formFlydown}
          visible={filterVisible}
          cleanFilters={this.cleanFilters}
          getInitial={this.getInitial}
          handleFilterToggle={this.handleFilterToggle}
          handleSearch={this.props.handleSearch}
          formattedCount={
            this.props.pagination && this.props.pagination.formattedCount
          }
          siteUser={siteUser}
        />
        <div className={classes.results}>{children}</div>
        <div className={classes.bottom}>{this.renderPagination()}</div>
        <div
          className={classNames(
            classes.overlay,
            filterVisible && classes.overlayActive
          )}
          onClick={this.handleFilterToggle}
        />
      </div>
    );
  }
}

export default withStyles((theme) => ({
  searchControlRoot: {
    position: "relative",
  },
  searchControlContainer: {
    position: "relative",
    width: "100%",
    padding: "0 24px",
    backgroundColor: theme.colors["white"],
    boxShadow:
      "0 6px 9px -2px rgba(0,0,0,.2), 0 9px 7px 0 rgba(0,0,0,0.01), 0 3px 4px 0 rgba(0,0,0,.02)",
    zIndex: theme.zIndex["searchControls"],
  },
  top: {
    display: "flex",
    margin: "0 auto",
    flexFlow: "row wrap",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    maxWidth: "1172px",
    position: "relative",
  },
  topLeft: {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "flex-start",
    alignItems: "center",
    paddingTop: 10,

    "&> * + *": {
      marginLeft: "0",
      marginTop: 8,
      [theme.breakpoints(318)]: {
        marginTop: 0,
      },
      [theme.breakpoints(330)]: {
        marginLeft: theme.spacing.unit * 0.5,
      },
    },
  },
  topRight: {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 0,
    marginTop: 10,
    "&> * + *": {
      marginLeft: "0",

      [theme.breakpoints(396)]: {
        marginTop: 8,
      },
      [theme.breakpoints(424)]: {
        marginTop: 0,
      },
      [theme.breakpoints(600)]: {
        marginLeft: theme.spacing.unit * 0.5,
      },
    },
  },
  clearFiltersButton: {
    marginLeft: 0,
    fontSize: "14px",
    fontWeight: "600",
    lineHeight: "1.6",
    padding: "12px 12px 12px 0", // padding "12px"
    borderColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  formFlydown: {
    marginTop: theme.spacing.unit,
  },
  bottom: {
    display: "flex",
    justifyContent: "center",
    margin: "0 auto",
    width: "100%",
    backgroundColor: "#fff",

    "&> * + *": {
      marginLeft: theme.spacing.unit * 0.5,
    },
  },
  sortOrderForm: {
    margin: "0 0 20px 0",
    [theme.breakpoints(396)]: {
      margin: "0 4px 0 0",
    },
    [theme.breakpoints(600)]: {
      margin: 0,
    },
  },
  overlay: {
    display: "none",
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100vh",
    backgroundColor: "#000000",
    opacity: "0.25",
    zIndex: theme.zIndex["loading"],
  },
  overlayActive: {
    display: "block",
  },
  moreButton: {
    height: 40,
    margin: "8px auto 16px",
  },
}))(withGlobalContext(SearchControls));
